<template>
  <div class="modal-overlay">
    <div class="modal-content-large" @click.stop>
      <div class="modal-header">
        <div class="modal-header-content">
          <h2>{{ company ? $t('modals.editCompany') : $t('modals.addCompany') }}</h2>
        </div>
        <button @click="$emit('close')" class="btn btn-icon">
          <LineIcon name="x" :size="15" :stroke-width="1.8" />
        </button>
      </div>

      <!-- Tab Navigation -->
      <div v-if="company" class="tab-navigation">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
        >
          <LineIcon :name="tab.icon" :size="15" :stroke-width="1.8" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="modal-body">
        <!-- Details Tab -->
        <div v-show="activeTab === 'details'" class="tab-content">
          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label for="name">{{ $t('modals.companyName') }} <span class="required">{{ $t('modals.required') }}</span></label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                :placeholder="$t('modals.enterCompanyName')"
                required
              />
            </div>

            <div class="form-group">
              <label for="alias_names">{{ $t('modals.aliasNames') }}</label>
              <div class="alias-names-container">
                <div class="alias-tags">
                  <span
                    v-for="(alias, index) in form.alias_names"
                    :key="index"
                    class="alias-tag"
                  >
                    {{ alias }}
                    <button
                      type="button"
                      @click="removeAlias(index)"
                      class="alias-remove"
                    >
                      ×
                    </button>
                  </span>
                  <input
                    v-model="newAlias"
                    @keydown.enter.prevent="addAlias"
                    @keydown.,="addAlias"
                    type="text"
                    class="alias-input"
                    :placeholder="$t('modals.addAliasPlaceholder')"
                  />
                </div>
                <p class="field-hint">{{ $t('modals.aliasHint') }}</p>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="vat_number">{{ $t('modals.vatNumber') }}</label>
                <input
                  id="vat_number"
                  v-model="form.vat_number"
                  type="text"
                  placeholder="BG123456789"
                />
              </div>

              <div class="form-group">
                <label for="registration_number">{{ $t('modals.registrationNumber') }}</label>
                <input
                  id="registration_number"
                  v-model="form.registration_number"
                  type="text"
                  placeholder="123456789"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="address">{{ $t('modals.address') }}</label>
              <input
                id="address"
                v-model="form.address"
                type="text"
                :placeholder="$t('modals.enterAddress')"
              />
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="city">{{ $t('modals.city') }}</label>
                <input
                  id="city"
                  v-model="form.city"
                  type="text"
                  :placeholder="$t('modals.city')"
                />
              </div>

              <div class="form-group">
                <label for="country">{{ $t('modals.country') }}</label>
                <input
                  id="country"
                  v-model="form.country"
                  type="text"
                  :placeholder="$t('modals.country')"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="phone">{{ $t('modals.phone') }}</label>
                <input
                  id="phone"
                  v-model="form.phone"
                  type="tel"
                  placeholder="+359 ..."
                />
              </div>

              <div class="form-group">
                <label for="email">{{ $t('modals.email') }}</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  placeholder="company@example.com"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="website">{{ $t('modals.website') }}</label>
                <input
                  id="website"
                  v-model="form.website"
                  type="url"
                  placeholder="https://..."
                />
              </div>

              <div class="form-group">
                <label for="tax_office">{{ $t('modals.taxOffice') }}</label>
                <input
                  id="tax_office"
                  v-model="form.tax_office"
                  type="text"
                  :placeholder="$t('modals.taxOffice')"
                />
              </div>
            </div>

            <div class="form-group form-group-toggle">
              <label class="toggle">
                <input type="checkbox" v-model="form.is_active" />
                <span class="toggle-track"></span>
              </label>
              <span class="toggle-text">{{ $t('modals.active') }}</span>
            </div>

            <div class="modal-footer">
              <div class="footer-left">
                <button
                  v-if="company"
                  type="button"
                  @click="showSaveAsAliasModal = true"
                  class="btn-save-as-alias"
                >
                  <LineIcon name="tag" :size="15" :stroke-width="1.8" />
                  {{ $t('modals.saveAsAlias') }}
                </button>
              </div>
              <div class="footer-right">
                <button type="button" @click="$emit('close')" class="btn-secondary">
                  {{ $t('modals.cancel') }}
                </button>
                <button type="submit" class="btn-primary" :disabled="saving">
                  {{ saving ? $t('modals.saving') : $t('modals.saveCompany') }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Vendors Tab -->
        <div v-show="activeTab === 'vendors'" class="tab-content">
          <div class="tab-header">
            <h3>{{ $t('modals.vendors') }}</h3>
            <div class="header-actions">
              <div class="search-box">
                <LineIcon name="search" :size="15" :stroke-width="1.8" class="search-icon" />
                <input
                  v-model="vendorSearch"
                  type="text"
                  :placeholder="$t('modals.searchVendors')"
                  class="search-input"
                />
              </div>
              <button @click="showVendorBulkImportModal = true" class="btn-secondary">
                <LineIcon name="file-import" :size="15" :stroke-width="1.8" />
                {{ $t('vendors.bulkImport', 'Bulk Import') }}
              </button>
              <button @click="showVendorModal = true" class="btn-add">
                <LineIcon name="plus" :size="15" :stroke-width="1.8" />
                {{ $t('modals.addVendor') }}
              </button>
            </div>
          </div>

          <div v-if="loadingVendors" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('modals.loadingVendors') }}</p>
          </div>

          <div v-else-if="vendors.length === 0" class="empty-state">
            <p>{{ $t('modals.noVendorsYet') }}</p>
            <button @click="showVendorModal = true" class="btn-primary">{{ $t('modals.addFirstVendor') }}</button>
          </div>

          <div v-else-if="filteredVendors.length === 0" class="empty-state">
            <p>{{ $t('modals.noVendorsFound', { search: vendorSearch }) }}</p>
          </div>

          <div v-else class="vendors-table">
            <!-- Select All Row -->
            <div class="select-row">
              <div class="select-all-section">
                <input
                  id="selectAllVendors"
                  type="checkbox"
                  @change="toggleSelectAllVendors"
                  :checked="selectedVendors.length === filteredVendors.length && filteredVendors.length > 0"
                />
                <label for="selectAllVendors">
                  {{ $t('common.selectAll') }} ({{ filteredVendors.length }})
                </label>
              </div>

              <!-- Bulk Actions when items selected -->
              <div v-if="selectedVendors.length > 0" class="bulk-actions">
                <button @click="bulkDeleteVendors" class="btn-bulk-delete">
                  <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                  {{ $t('common.deleteSelected') }} ({{ selectedVendors.length }})
                </button>
              </div>

              <!-- Vendor Count -->
              <div v-else class="scan-count">
                {{ $t('common.showing') }} {{ filteredVendors.length }} {{ $t('common.of') }} {{ vendors.length }} {{ $t('modals.vendors') }}
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th class="checkbox-column"></th>
                  <th @click="toggleVendorSort('vendor_name')" class="sortable-header">
                    {{ $t('modals.vendorName') }}
                    <LineIcon :name="vendorSort.field === 'vendor_name' ? (vendorSort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down'" :size="12" :stroke-width="1.8" />
                  </th>
                  <th @click="toggleVendorSort('vendor_code')" class="sortable-header">
                    {{ $t('modals.vendorCode') }}
                    <LineIcon :name="vendorSort.field === 'vendor_code' ? (vendorSort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down'" :size="12" :stroke-width="1.8" />
                  </th>
                  <th @click="toggleVendorSort('vendor_vat')" class="sortable-header">
                    {{ $t('modals.vatNumber') }}
                    <LineIcon :name="vendorSort.field === 'vendor_vat' ? (vendorSort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down'" :size="12" :stroke-width="1.8" />
                  </th>
                  <th class="actions-column">{{ $t('modals.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="vendor in filteredVendors" :key="vendor.id">
                  <td class="checkbox-column">
                    <input
                      type="checkbox"
                      :checked="selectedVendors.includes(vendor.id)"
                      @change="toggleVendorSelection(vendor.id, $event)"
                    />
                  </td>
                  <td>{{ vendor.vendor_name }}</td>
                  <td>{{ vendor.vendor_code || '—' }}</td>
                  <td>{{ vendor.vendor_vat || '—' }}</td>
                  <td class="actions-column">
                    <div class="actions-wrapper">
                      <button @click="editVendor(vendor)" class="btn-icon" title="Edit">
                        <LineIcon name="pencil" :size="15" :stroke-width="1.8" />
                      </button>
                      <button @click="deleteVendor(vendor)" class="btn-icon btn-icon-danger" title="Delete">
                        <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Clients Tab -->
        <div v-if="clientsEnabledForCompany" v-show="activeTab === 'clients'" class="tab-content">
          <div class="tab-header">
            <h3>{{ $t('modals.clients') }}</h3>
            <div class="header-actions">
              <div class="search-box">
                <LineIcon name="search" :size="15" :stroke-width="1.8" class="search-icon" />
                <input
                  v-model="clientSearch"
                  type="text"
                  :placeholder="$t('modals.searchClients')"
                  class="search-input"
                />
              </div>
              <button @click="showClientBulkImportModal = true" class="btn-secondary">
                <LineIcon name="file-import" :size="15" :stroke-width="1.8" />
                {{ $t('clients.bulkImport', 'Bulk Import') }}
              </button>
              <button @click="showClientModal = true" class="btn-add">
                <LineIcon name="plus" :size="15" :stroke-width="1.8" />
                {{ $t('modals.addClient') }}
              </button>
            </div>
          </div>

          <div v-if="loadingClients" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('modals.loadingClients') }}</p>
          </div>

          <div v-else-if="clients.length === 0" class="empty-state">
            <p>{{ $t('modals.noClientsYet') }}</p>
            <button @click="showClientModal = true" class="btn-primary">{{ $t('modals.addFirstClient') }}</button>
          </div>

          <div v-else-if="filteredClients.length === 0" class="empty-state">
            <p>{{ $t('modals.noClientsFound', { search: clientSearch }) }}</p>
          </div>

          <div v-else class="vendors-table">
            <div class="select-row">
              <div class="select-all-section">
                <input
                  id="selectAllClients"
                  type="checkbox"
                  @change="toggleSelectAllClients"
                  :checked="selectedClients.length === filteredClients.length && filteredClients.length > 0"
                />
                <label for="selectAllClients">
                  {{ $t('common.selectAll') }} ({{ filteredClients.length }})
                </label>
              </div>

              <div v-if="selectedClients.length > 0" class="bulk-actions">
                <button @click="bulkDeleteClients" class="btn-bulk-delete">
                  <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                  {{ $t('common.deleteSelected') }} ({{ selectedClients.length }})
                </button>
              </div>

              <div v-else class="scan-count">
                {{ $t('common.showing') }} {{ filteredClients.length }} {{ $t('common.of') }} {{ clients.length }} {{ $t('modals.clients') }}
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th class="checkbox-column"></th>
                  <th @click="toggleClientSort('vendor_name')" class="sortable-header">
                    {{ $t('modals.clientName') }}
                    <LineIcon :name="clientSort.field === 'vendor_name' ? (clientSort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down'" :size="12" :stroke-width="1.8" />
                  </th>
                  <th @click="toggleClientSort('vendor_code')" class="sortable-header">
                    {{ $t('modals.clientCode') }}
                    <LineIcon :name="clientSort.field === 'vendor_code' ? (clientSort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down'" :size="12" :stroke-width="1.8" />
                  </th>
                  <th @click="toggleClientSort('vendor_vat')" class="sortable-header">
                    {{ $t('modals.vatNumber') }}
                    <LineIcon :name="clientSort.field === 'vendor_vat' ? (clientSort.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-up-down'" :size="12" :stroke-width="1.8" />
                  </th>
                  <th class="actions-column">{{ $t('modals.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="client in filteredClients" :key="client.id">
                  <td class="checkbox-column">
                    <input
                      type="checkbox"
                      :checked="selectedClients.includes(client.id)"
                      @change="toggleClientSelection(client.id, $event)"
                    />
                  </td>
                  <td>{{ client.vendor_name }}</td>
                  <td>{{ client.vendor_code || '—' }}</td>
                  <td>{{ client.vendor_vat || '—' }}</td>
                  <td class="actions-column">
                    <div class="actions-wrapper">
                      <button @click="editClient(client)" class="btn-icon" title="Edit">
                        <LineIcon name="pencil" :size="15" :stroke-width="1.8" />
                      </button>
                      <button @click="deleteClient(client)" class="btn-icon btn-icon-danger" title="Delete">
                        <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Account Codes Tab -->
        <div v-show="activeTab === 'account-codes'" class="tab-content">
          <div class="tab-header">
            <h3>{{ $t('modals.accountCodes') }}</h3>
            <div class="header-actions">
              <div class="search-box">
                <LineIcon name="search" :size="15" :stroke-width="1.8" class="search-icon" />
                <input
                  v-model="accountCodeSearch"
                  type="text"
                  :placeholder="$t('modals.searchAccountCodes')"
                  class="search-input"
                />
              </div>
              <button @click="showBulkImportModal = true" class="btn-secondary">
                <LineIcon name="file-import" :size="15" :stroke-width="1.8" />
                {{ $t('accountCodes.bulkImport.button') }}
              </button>
              <button @click="showAccountCodeModal = true" class="btn-add">
                <LineIcon name="plus" :size="15" :stroke-width="1.8" />
                {{ $t('modals.addCode') }}
              </button>
            </div>
          </div>

          <div v-if="loadingAccountCodes" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('modals.loadingAccountCodes') }}</p>
          </div>

          <div v-else-if="accountCodes.length === 0" class="empty-state">
            <p>{{ $t('modals.noAccountCodesYet') }}</p>
            <button @click="showAccountCodeModal = true" class="btn-primary">{{ $t('modals.addFirstCode') }}</button>
          </div>

          <div v-else-if="filteredAccountCodes.length === 0" class="empty-state">
            <p>{{ $t('modals.noAccountCodesFound', { search: accountCodeSearch }) }}</p>
          </div>

          <div v-else class="account-codes-table">
            <!-- Select All Row -->
            <div class="select-row">
              <div class="select-all-section">
                <input
                  id="selectAllAccountCodes"
                  type="checkbox"
                  @change="toggleSelectAll"
                  :checked="selectedAccountCodes.length === filteredAccountCodes.length && filteredAccountCodes.length > 0"
                />
                <label for="selectAllAccountCodes">
                  {{ $t('common.selectAll') }} ({{ groupedAccountCodes.length }})
                </label>
              </div>

              <!-- Bulk Actions - Show when items selected -->
              <div v-if="selectedAccountCodes.length > 0" class="bulk-actions">
                <button @click="bulkDeleteAccountCodes" class="btn-bulk-delete">
                  <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                  {{ $t('common.deleteSelected') }} ({{ selectedAccountCodes.length }})
                </button>
              </div>

              <!-- Account Code Count - Show when no selection -->
              <div v-else class="scan-count">
                {{ $t('common.showing') }} {{ groupedAccountCodes.length }} {{ $t('common.of') }} {{ accountCodes.length }} {{ $t('modals.accountCodes') }}
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th class="checkbox-column"></th>
                  <th>Code</th>
                  <th>Description</th>
                  <th class="internal-column">{{ $t('accountCodes.internalCode') }}</th>
                  <th>Type</th>
                  <th class="actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in groupedAccountCodes" :key="group.account_code">
                  <td class="checkbox-column">
                    <input
                      type="checkbox"
                      :value="group.ids"
                      @change="toggleGroupSelection(group.ids, $event)"
                      :checked="group.ids.every(id => selectedAccountCodes.includes(id))"
                    />
                  </td>
                  <td>{{ group.account_code }}</td>
                  <td>
                    <span v-if="group.totalMappings > 0" class="mapping-badge">
                      {{ group.displayName }}
                    </span>
                    <span v-else>{{ group.account_name || '—' }}</span>
                  </td>
                  <td class="internal-column">
                    <span
                      v-if="group.internalCode"
                      class="internal-badge"
                      :class="`internal-badge-${group.internalCode.toLowerCase()}`"
                    >{{ group.internalCode }}</span>
                    <span v-else class="internal-empty">—</span>
                  </td>
                  <td>{{ group.account_type || '—' }}</td>
                  <td class="actions-column">
                    <button @click="editAccountCode(group.entries[0])" class="btn-icon" title="Edit">
                      <LineIcon name="pencil" :size="15" :stroke-width="1.8" />
                    </button>
                    <button @click="deleteAccountCodeGroup(group)" class="btn-icon btn-icon-danger" title="Delete">
                      <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Payment Codes Tab -->
        <div v-show="activeTab === 'payment-codes'" class="tab-content">
          <div class="tab-header">
            <h3>{{ $t('paymentCodes.title') }}</h3>
          </div>

          <p class="pc-intro">{{ $t('paymentCodes.intro') }}</p>

          <div class="pc-table">
            <div class="pc-row pc-head">
              <span>{{ $t('paymentCodes.method') }}</span>
              <span>{{ $t('paymentCodes.code') }}</span>
              <span>{{ $t('paymentCodes.default') }}</span>
            </div>
            <div v-for="method in paymentMethodCodes" :key="method.key" class="pc-row">
              <span class="pc-method">{{ $t(`paymentCodes.methods.${method.key}`) }}</span>
              <input
                v-model="paymentTypeCodes[method.key]"
                type="text"
                maxlength="20"
                class="pc-input"
                :placeholder="method.default"
              />
              <span class="pc-default">{{ method.default }}</span>
            </div>
          </div>

          <p class="pc-hint">{{ $t('paymentCodes.hint') }}</p>

          <div class="modal-footer">
            <div class="footer-left">
              <button type="button" class="btn-secondary" :disabled="savingPaymentCodes" @click="resetPaymentCodes">
                {{ $t('paymentCodes.reset') }}
              </button>
            </div>
            <div class="footer-right">
              <button type="button" class="btn-primary" :disabled="savingPaymentCodes" @click="savePaymentCodes">
                {{ savingPaymentCodes ? $t('modals.saving') : $t('paymentCodes.save') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Archive Tab -->
        <div v-show="activeTab === 'archive'" class="tab-content">
          <div class="tab-header">
            <h3>{{ $t('modals.companyInvoices') }}</h3>
            <div class="header-actions">
              <div class="filter-container">
                <div class="search-box">
                  <LineIcon name="search" :size="15" :stroke-width="1.8" class="search-icon" />
                  <input
                    v-model="invoiceSearch"
                    type="text"
                    :placeholder="$t('modals.searchInvoices')"
                    class="search-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Comprehensive Filter Row -->
          <div class="filters-row">
            <div class="filter-item">
              <label for="filterStatus">{{ $t('common.status', 'Status') }}</label>
              <SelectField v-model="filterStatus" :options="statusOptions" class="filter-select" />
            </div>

            <div class="filter-item">
              <label for="filterVendor">{{ $t('common.vendor', 'Vendor') }}</label>
              <SelectField v-model="filterVendor" :options="vendorFilterOptions" class="filter-select" />
            </div>

            <div class="filter-item">
              <label for="filterType">{{ $t('common.type', 'Type') }}</label>
              <SelectField v-model="filterType" :options="typeOptions" class="filter-select" />
            </div>

            <div class="filter-item">
              <label for="filterVerified">{{ $t('common.verified', 'Verified') }}</label>
              <SelectField v-model="filterVerified" :options="verifiedOptions" class="filter-select" />
            </div>

            <div class="filter-item">
              <label for="filterUploadDate">{{ $t('common.uploadDate', 'Upload Date') }}</label>
              <DateField v-model="filterUploadDate" class="filter-input" />
            </div>

            <div class="filter-item">
              <label for="filterDateFrom">{{ $t('common.dateFrom', 'Date From') }}</label>
              <DateField v-model="filterDateFrom" class="filter-input" />
            </div>

            <div class="filter-item">
              <label for="filterDateTo">{{ $t('common.dateTo', 'Date To') }}</label>
              <DateField v-model="filterDateTo" class="filter-input" />
            </div>

            <div class="filter-item">
              <button @click="clearFilters" class="btn-clear-filters">
                <LineIcon name="x-circle" :size="15" :stroke-width="1.8" />
                {{ $t('common.clearFilters', 'Clear Filters') }}
              </button>
            </div>
          </div>

          <div v-if="loadingInvoices" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('modals.loadingInvoices') }}</p>
          </div>

          <div v-else-if="invoices.length === 0" class="empty-state">
            <p>{{ $t('modals.noInvoicesYet') }}</p>
          </div>

          <div v-else-if="filteredInvoices.length === 0" class="empty-state">
            <p>{{ $t('modals.noInvoicesFound', { search: invoiceSearch }) }}</p>
          </div>

          <div v-else class="invoices-table">
            <!-- Select All Row -->
            <div class="select-row">
              <div class="select-all-section">
                <input
                  id="selectAllInvoices"
                  type="checkbox"
                  @change="toggleSelectAllInvoices"
                  :checked="selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0"
                />
                <label for="selectAllInvoices">
                  {{ $t('common.selectAll') }} ({{ filteredInvoices.length }})
                </label>
              </div>

              <!-- Bulk Actions - Show when items selected -->
              <div v-if="selectedInvoices.length > 0" class="bulk-actions">
                <button @click="bulkDownloadInvoices" class="btn-bulk-download">
                  <LineIcon name="download" :size="15" :stroke-width="1.8" />
                  {{ $t('common.download') }} ({{ selectedInvoices.length }})
                </button>
                <button @click="bulkDeleteInvoices" class="btn-bulk-delete">
                  <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                  {{ $t('common.delete') }} ({{ selectedInvoices.length }})
                </button>
              </div>

              <!-- Invoice Count - Show when no selection -->
              <div v-else class="scan-count">
                {{ $t('common.showing') }} {{ filteredInvoices.length }} {{ $t('common.of') }} {{ invoices.length }} {{ $t('modals.invoices') }}
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th class="checkbox-column"></th>
                  <th>{{ $t('modals.invoiceNumber') }}</th>
                  <th>{{ $t('modals.vendor') }}</th>
                  <th>{{ $t('modals.date') }}</th>
                  <th>{{ $t('modals.amount') }}</th>
                  <th>{{ $t('modals.status') }}</th>
                  <th class="actions-column">{{ $t('modals.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in invoicesGroupedByDate" :key="group.date">
                  <!-- Date Separator Row -->
                  <tr class="date-separator-row">
                    <td colspan="7">
                      <div class="date-header">
                        <input
                          type="checkbox"
                          :checked="isDateSelected(group)"
                          @change="toggleDateSelection(group)"
                          class="date-checkbox"
                        />
                        <span class="date-label">{{ group.label }}</span>
                        <span class="date-count">({{ group.invoices.length }})</span>
                      </div>
                    </td>
                  </tr>

                  <!-- Invoice Rows -->
                  <tr v-for="invoice in group.invoices" :key="invoice.id">
                    <td class="checkbox-column" data-label="Select">
                      <input
                        type="checkbox"
                        :value="invoice.id"
                        v-model="selectedInvoices"
                      />
                    </td>
                    <td data-label="Invoice #">{{ invoice.invoice_number || '—' }}</td>
                    <td data-label="Vendor">{{ invoice.vendor_name || '—' }}</td>
                    <td data-label="Date">{{ formatDate(invoice.invoice_date) }}</td>
                    <td data-label="Amount">{{ formatAmount(invoice.total_amount, invoice.currency) }}</td>
                    <td data-label="Status">
                      <span :class="['status-badge', `status-${invoice.status || 'pending'}`]">
                        {{ invoice.status || 'pending' }}
                      </span>
                    </td>
                    <td class="actions-column">
                      <div class="invoice-actions">
                        <button @click.stop="viewInvoice(invoice)" class="btn-action btn-view" :title="$t('common.view')">
                          <LineIcon name="eye" :size="15" :stroke-width="1.8" />
                        </button>
                        <button @click.stop="exportInvoice(invoice)" class="btn-action btn-export" :title="$t('common.export')">
                          <LineIcon name="download" :size="15" :stroke-width="1.8" />
                        </button>
                        <button @click="deleteInvoice(invoice)" class="btn-action btn-delete" :title="$t('common.delete')">
                          <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>

    <!-- Save as Alias Modal -->
    <SaveAsAliasModal
      v-if="showSaveAsAliasModal"
      :company="company"
      @close="showSaveAsAliasModal = false"
      @saved="handleAliasAdded"
    />

    <!-- Vendor Modal -->
    <VendorModal
      v-if="showVendorModal"
      :vendor="editingVendor"
      :companyId="company?.id"
      @close="closeVendorModal"
      @saved="handleVendorSaved"
    />

    <!-- Bulk Import Vendors Modal -->
    <BulkImportVendorsModal
      v-if="showVendorBulkImportModal"
      :companyId="company?.id"
      @close="showVendorBulkImportModal = false"
      @imported="handleVendorBulkImported"
    />

    <!-- Client Modal -->
    <ClientModal
      v-if="showClientModal"
      :client="editingClient"
      :companyId="company?.id"
      @close="closeClientModal"
      @saved="handleClientSaved"
    />

    <!-- Bulk Import Clients Modal -->
    <BulkImportClientsModal
      v-if="showClientBulkImportModal"
      :companyId="company?.id"
      @close="showClientBulkImportModal = false"
      @imported="handleClientBulkImported"
    />

    <!-- Account Code Modal -->
    <AccountCodeModal
      v-if="showAccountCodeModal"
      :key="editingAccountCode?.id || 'new'"
      :accountCode="editingAccountCode"
      :companyId="company?.id"
      :canGoPrevious="canGoPreviousAccountCode"
      :canGoNext="canGoNextAccountCode"
      @close="closeAccountCodeModal"
      @saved="handleAccountCodeSaved"
      @navigate="navigateAccountCode"
    />

    <!-- Bulk Import Account Codes Modal -->
    <BulkImportAccountCodesModal
      v-if="showBulkImportModal"
      :companyId="company?.id"
      @close="showBulkImportModal = false"
      @imported="handleBulkImported"
    />

    <!-- Code Mapping Modal -->
    <AccountCodeModal
      v-if="showCodeMappingModal"
      :accountCode="editingCodeMapping"
      :companyId="company?.id"
      :mappingMode="true"
      @close="closeCodeMappingModal"
      @saved="handleCodeMappingSaved"
    />

    <!-- Invoice Preview Modal -->
    <div v-if="showInvoicePreview" class="invoice-preview-modal-overlay" @click="closePreview">
      <div class="invoice-preview-modal" @click.stop>
        <div class="preview-header">
          <h3>{{ previewInvoice?.invoice_number || 'Invoice Preview' }}</h3>
          <div class="preview-actions">
            <button @click="printInvoice" class="btn-print" :title="$t('common.print')">
              <LineIcon name="printer" :size="15" :stroke-width="1.8" />
              {{ $t('common.print') }}
            </button>
            <button @click="closePreview" class="btn-close" :title="$t('common.close')">
              <LineIcon name="x" :size="15" :stroke-width="1.8" />
            </button>
          </div>
        </div>
        <div class="preview-content">
          <img 
            v-if="previewInvoice?.id"
            :src="`/api/invoices/${previewInvoice.id}/original?thumbnail=false`"
            :alt="previewInvoice?.invoice_number || 'Invoice'"
            class="preview-image"
          />
          <div v-else class="preview-placeholder">
            <p>{{ $t('common.loadingPreview', 'Loading preview...') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import SelectField from '@/components/SelectField.vue';
import DateField from '@/components/DateField.vue';
import { ref, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import api from '@/utils/api';
import SaveAsAliasModal from './SaveAsAliasModal.vue';
import VendorModal from './VendorModal.vue';
import ClientModal from './ClientModal.vue';
import AccountCodeModal from './AccountCodeModal.vue';
import BulkImportAccountCodesModal from './BulkImportAccountCodesModal.vue';
import BulkImportVendorsModal from './BulkImportVendorsModal.vue';
import BulkImportClientsModal from './BulkImportClientsModal.vue';
import { useAuthStore } from '@/stores/auth';

const props = defineProps({
  company: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();

const clientsEnabledForCompany = computed(() => {
  if (!authStore.useClients) return false;
  const ids = authStore.clientsCompanyIds || [];
  if (ids.length === 0) return true;
  const cid = props.company?.id;
  return cid != null && ids.includes(cid);
});

const activeTab = ref('details');
const tabs = computed(() => {
  /* Each tab names its mark from the app's own set, rather than carrying a
     hand-written Heroicons path at stroke 2 — those were the only glyphs in
     the product drawn at a different weight than everything around them. */
  const list = [
    { id: 'details', label: t('modals.details'), icon: 'building' },
    { id: 'vendors', label: t('modals.vendors'), icon: 'users' },
  ];

  if (clientsEnabledForCompany.value) {
    list.push({ id: 'clients', label: t('modals.clients'), icon: 'user-tie' });
  }

  list.push(
    { id: 'account-codes', label: t('modals.accountCodes'), icon: 'file-text' },
    { id: 'payment-codes', label: t('paymentCodes.tab'), icon: 'credit-card' },
    { id: 'archive', label: t('modals.archive'), icon: 'archive' },
  );

  return list;
});

const form = ref({
  name: '',
  alias_names: [],
  vat_number: '',
  address: '',
  city: '',
  country: '',
  phone: '',
  email: '',
  website: '',
  registration_number: '',
  tax_office: '',
  is_active: true,
  validate_company_on_scan: false
});

const saving = ref(false);
const newAlias = ref('');
const showSaveAsAliasModal = ref(false);
const showVendorModal = ref(false);
const showVendorBulkImportModal = ref(false);
const showClientModal = ref(false);
const showClientBulkImportModal = ref(false);
const showAccountCodeModal = ref(false);
const showBulkImportModal = ref(false);

const vendors = ref([]);
const loadingVendors = ref(false);
const editingVendor = ref(null);

const clients = ref([]);
const loadingClients = ref(false);
const editingClient = ref(null);

const accountCodes = ref([]);
const loadingAccountCodes = ref(false);
const editingAccountCode = ref(null);

const vendorSearch = ref('');
const vendorSort = ref({ field: 'vendor_name', direction: 'asc' });
const selectedVendors = ref([]);
const clientSearch = ref('');
const clientSort = ref({ field: 'vendor_name', direction: 'asc' });
const selectedClients = ref([]);
const accountCodeSearch = ref('');
const codeMappingSearch = ref('');
const selectedAccountCodes = ref([]);
const showCodeMappingModal = ref(false);
const editingCodeMapping = ref(null);

const invoices = ref([]);
const loadingInvoices = ref(false);
const invoiceSearch = ref('');
const selectedInvoices = ref([]);
const showInvoicePreview = ref(false);
const previewInvoice = ref(null);

// Filter state
const filterStatus = ref('');
const filterVendor = ref('');
const filterType = ref('');
const filterVerified = ref('');
const filterDateFrom = ref('');
const filterDateTo = ref('');
const filterUploadDate = ref('');
const filterArchived = ref('');

/* What the filters offer, as lists rather than <option> tags: the fields are
   the app's own dropdowns now, and the browser's list is not drawn by us. */
const statusOptions = computed(() => [
  { value: '', label: t('common.allStatuses', 'All Statuses') },
  { value: 'pending', label: t('common.pending', 'Pending') },
  { value: 'processed', label: t('common.processed', 'Processed') },
  { value: 'archived', label: t('common.archived', 'Archived') },
  { value: 'failed', label: t('common.failed', 'Failed') },
]);

const vendorFilterOptions = computed(() => [
  { value: '', label: t('common.allVendors', 'All Vendors') },
  ...vendors.value.map((vendor) => ({
    value: vendor.vendor_name,
    label: vendor.vendor_name,
  })),
]);

const typeOptions = computed(() => [
  { value: '', label: t('common.allTypes', 'All Types') },
  { value: 'purchase', label: t('common.purchase', 'Purchase') },
  { value: 'sale', label: t('common.sale', 'Sale') },
  { value: 'expense', label: t('common.expense', 'Expense') },
]);

const verifiedOptions = computed(() => [
  { value: '', label: t('common.all', 'All') },
  { value: 'yes', label: t('common.yes', 'Yes') },
  { value: 'no', label: t('common.no', 'No') },
]);

const filteredVendors = computed(() => {
  let list = vendors.value;
  const search = vendorSearch.value.trim().toLowerCase();
  if (search) {
    list = list.filter(vendor =>
      vendor.vendor_name?.toLowerCase().includes(search) ||
      vendor.vendor_code?.toString().toLowerCase().includes(search) ||
      vendor.vendor_vat?.toLowerCase().includes(search) ||
      vendor.vendor_tax_id?.toLowerCase().includes(search) ||
      vendor.vendor_email?.toLowerCase().includes(search) ||
      vendor.vendor_phone?.toLowerCase().includes(search)
    );
  }

  const { field, direction } = vendorSort.value;
  const dir = direction === 'desc' ? -1 : 1;
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return [...list].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    const aEmpty = av === null || av === undefined || av === '';
    const bEmpty = bv === null || bv === undefined || bv === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    return dir * collator.compare(String(av), String(bv));
  });
});

const toggleVendorSort = (field) => {
  if (vendorSort.value.field === field) {
    vendorSort.value.direction = vendorSort.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    vendorSort.value.field = field;
    vendorSort.value.direction = 'asc';
  }
};

const filteredClients = computed(() => {
  let list = clients.value;
  const search = clientSearch.value.trim().toLowerCase();
  if (search) {
    list = list.filter(client =>
      client.vendor_name?.toLowerCase().includes(search) ||
      client.vendor_code?.toString().toLowerCase().includes(search) ||
      client.vendor_vat?.toLowerCase().includes(search) ||
      client.vendor_tax_id?.toLowerCase().includes(search) ||
      client.vendor_email?.toLowerCase().includes(search) ||
      client.vendor_phone?.toLowerCase().includes(search)
    );
  }

  const { field, direction } = clientSort.value;
  const dir = direction === 'desc' ? -1 : 1;
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return [...list].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    const aEmpty = av === null || av === undefined || av === '';
    const bEmpty = bv === null || bv === undefined || bv === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    return dir * collator.compare(String(av), String(bv));
  });
});

const toggleClientSort = (field) => {
  if (clientSort.value.field === field) {
    clientSort.value.direction = clientSort.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    clientSort.value.field = field;
    clientSort.value.direction = 'asc';
  }
};

/**
 * The Internal Code field of the account code, and nothing else.
 *
 * A barcode or description mapped to the code can carry an internal code of its
 * own, set behind the pencil next to the mapping field. Those are deliberately
 * left out: the column exists so the list reads the same as the form, and
 * anything the form does not show would make the two disagree. Empty stays
 * empty — a dash — rather than borrowing a value from a keyword.
 */
const accountCodeInternalCode = (entries) => {
  for (const entry of entries || []) {
    const value = String(entry.internal_code || '').trim();
    if (value) return value;
  }
  return '';
};

const filteredAccountCodes = computed(() => {
  if (!accountCodeSearch.value) return accountCodes.value;
  const search = accountCodeSearch.value.toLowerCase();
  return accountCodes.value.filter(code =>
    code.account_code?.toLowerCase().includes(search) ||
    code.account_name?.toLowerCase().includes(search) ||
    code.account_type?.toLowerCase().includes(search) ||
    accountCodeInternalCode([code]).toLowerCase().includes(search)
  );
});

// Group account codes by account_code value
const groupedAccountCodes = computed(() => {
  const filtered = filteredAccountCodes.value;
  const groupMap = new Map();
  
  filtered.forEach(code => {
    const key = code.account_code;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        account_code: code.account_code,
        account_type: code.account_type,
        entries: [],
        allDescriptions: [],
        allBarcodes: [],
        ids: []
      });
    }
    
    const group = groupMap.get(key);
    group.entries.push(code);
    group.ids.push(code.id);
    
    // Collect all descriptions
    if (code.description_mapping && Array.isArray(code.description_mapping)) {
      group.allDescriptions.push(...code.description_mapping);
    }
    
    // Collect all barcodes
    if (code.barcode_mapping && Array.isArray(code.barcode_mapping)) {
      group.allBarcodes.push(...code.barcode_mapping);
    }
    
    // Prefer non-empty account_name
    if (code.account_name && code.account_name.trim()) {
      if (!group.account_name || group.account_name === 'Product Supplies') {
        group.account_name = code.account_name;
      }
    }
  });
  
  // Convert map to array and add summary
  return Array.from(groupMap.values()).map(group => {
    group.allDescriptions = [...new Set(group.allDescriptions)];
    group.allBarcodes = [...new Set(group.allBarcodes)];
    group.totalMappings = group.allDescriptions.length + group.allBarcodes.length;
    group.displayName = group.account_name || '—';
    group.internalCode = accountCodeInternalCode(group.entries);
    return group;
  });
});

const accountCodesWithMapping = computed(() => {
  return accountCodes.value.filter(code => code.barcode_mapping);
});

const filteredCodeMappings = computed(() => {
  if (!codeMappingSearch.value) return accountCodesWithMapping.value;
  const search = codeMappingSearch.value.toLowerCase();
  return accountCodesWithMapping.value.filter(code =>
    code.barcode_mapping?.toLowerCase().includes(search) ||
    code.account_code?.toLowerCase().includes(search) ||
    code.account_name?.toLowerCase().includes(search)
  );
});

const filteredInvoices = computed(() => {
  let filtered = invoices.value;

  // Apply search filter
  if (invoiceSearch.value) {
    const search = invoiceSearch.value.toLowerCase();
    filtered = filtered.filter(invoice =>
      invoice.invoice_number?.toLowerCase().includes(search) ||
      invoice.vendor_name?.toLowerCase().includes(search) ||
      invoice.total_amount?.toString().includes(search)
    );
  }

  // Apply status filter
  if (filterStatus.value) {
    filtered = filtered.filter(invoice => invoice.status === filterStatus.value);
  }

  // Apply vendor filter
  if (filterVendor.value) {
    filtered = filtered.filter(invoice => invoice.vendor_name === filterVendor.value);
  }

  // Apply type filter (assuming invoice.type exists)
  if (filterType.value) {
    filtered = filtered.filter(invoice => invoice.type === filterType.value);
  }

  // Apply verified filter (assuming invoice.verified exists)
  if (filterVerified.value) {
    filtered = filtered.filter(invoice => invoice.verified === filterVerified.value);
  }

  // Apply date range filter
  if (filterDateFrom.value) {
    const fromDate = new Date(filterDateFrom.value);
    filtered = filtered.filter(invoice => {
      const invoiceDate = new Date(invoice.invoice_date || invoice.created_at);
      return invoiceDate >= fromDate;
    });
  }

  if (filterDateTo.value) {
    const toDate = new Date(filterDateTo.value);
    toDate.setHours(23, 59, 59, 999); // Include entire day
    filtered = filtered.filter(invoice => {
      const invoiceDate = new Date(invoice.invoice_date || invoice.created_at);
      return invoiceDate <= toDate;
    });
  }

  // Apply upload date filter
  if (filterUploadDate.value) {
    const uploadDate = new Date(filterUploadDate.value);
    const nextDay = new Date(uploadDate);
    nextDay.setDate(nextDay.getDate() + 1);
    filtered = filtered.filter(invoice => {
      const createdDate = new Date(invoice.created_at);
      return createdDate >= uploadDate && createdDate < nextDay;
    });
  }

  // Apply archived filter (assuming invoice.archived exists)
  if (filterArchived.value) {
    filtered = filtered.filter(invoice => invoice.archived === filterArchived.value);
  }

  return filtered;
});

const invoicesGroupedByDate = computed(() => {
  const groups = [];
  const dateMap = new Map();

  filteredInvoices.value.forEach(invoice => {
    const date = new Date(invoice.invoice_date || invoice.created_at);
    const dateKey = date.toDateString();

    if (!dateMap.has(dateKey)) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let label;
      if (date.toDateString() === today.toDateString()) {
        label = t('common.today');
      } else if (date.toDateString() === yesterday.toDateString()) {
        label = t('common.yesterday');
      } else {
        label = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
      }

      const group = { date: dateKey, label, invoices: [] };
      dateMap.set(dateKey, group);
      groups.push(group);
    }

    dateMap.get(dateKey).invoices.push(invoice);
  });

  return groups;
});

// Function declarations (must be before watch)
const loadVendors = async () => {
  if (!props.company) return;

  try {
    loadingVendors.value = true;
    const response = await api.get(`/api/companies/${props.company.id}/vendors`, {
      params: { type: 'supplier' }
    });
    vendors.value = response.data.data || response.data;
  } catch (error) {
    console.error('Failed to load vendors:', error);
  } finally {
    loadingVendors.value = false;
  }
};

const loadClients = async () => {
  if (!props.company) return;

  try {
    loadingClients.value = true;
    const response = await api.get(`/api/companies/${props.company.id}/vendors`, {
      params: { type: 'client' }
    });
    clients.value = response.data.data || response.data;
  } catch (error) {
    console.error('Failed to load clients:', error);
  } finally {
    loadingClients.value = false;
  }
};

const loadAccountCodes = async () => {
  if (!props.company) return;

  try {
    loadingAccountCodes.value = true;
    const response = await api.get(`/api/companies/${props.company.id}/account-codes`);
    accountCodes.value = response.data.data || response.data;
  } catch (error) {
    console.error('Failed to load account codes:', error);
  } finally {
    loadingAccountCodes.value = false;
  }
};

const loadInvoices = async () => {
  if (!props.company) return;

  try {
    loadingInvoices.value = true;
    const response = await api.get(`/api/invoices`, {
      params: {
        company_id: props.company.id,
        status: 'archived'
      }
    });

    // Map the API response to the format expected by the template
    const rawInvoices = response.data.invoices || response.data.data || response.data;
    invoices.value = rawInvoices.map(inv => ({
      id: inv.id,
      invoice_number: inv.invoice_number,
      vendor_name: inv.seller_name || inv.vendor?.name || '—',
      invoice_date: inv.invoice_date,
      total_amount: inv.grand_total,
      currency: inv.currency || 'BGN',
      status: inv.status,
      file_path: inv.file_path || null,
      original_path: inv.original_path || inv.file_path || null
    }));
  } catch (error) {
    console.error('Failed to load invoices:', error);
  } finally {
    loadingInvoices.value = false;
  }
};

/**
 * Which code each payment method exports as.
 *
 * The defaults mirror Company::DEFAULT_PAYMENT_TYPE_CODES on the server, which
 * stays the authority: an empty field here means "use the default", and the
 * placeholder shows what that is, so nothing has to be typed to keep today's
 * behaviour. Stored per company — one client's nomenclature never reaches
 * another's exports.
 *
 * Plain numbers, deliberately. Punctuation a given accounting system wants
 * around the code belongs to that exporter — Santa, for instance, writes the
 * card code with a leading hash of its own accord.
 *
 * Declared above the watcher below, which loads them on open and runs
 * immediately.
 */
const PAYMENT_METHOD_DEFAULTS = [
  { key: 'Cash', default: '1' },
  { key: 'Bank Transfer', default: '2' },
  { key: 'Credit Card', default: '4' },
  { key: 'Check', default: '2' },
  { key: 'Online Payment', default: '2' },
  { key: 'Deferred Payment', default: '2' },
  { key: 'Partial Payment', default: '2' },
  { key: 'Other', default: '2' },
];

const paymentMethodCodes = PAYMENT_METHOD_DEFAULTS;
const paymentTypeCodes = ref({});
const savingPaymentCodes = ref(false);

const loadPaymentCodes = (company) => {
  const stored = company?.payment_type_codes || {};
  const next = {};
  for (const { key } of PAYMENT_METHOD_DEFAULTS) {
    next[key] = stored[key] ?? '';
  }
  paymentTypeCodes.value = next;
};

// Load company data if editing
watch(() => props.company, (company) => {
  if (company) {
    form.value = {
      ...company,
      alias_names: Array.isArray(company.alias_names) ? company.alias_names : []
    };
    loadVendors();
    if (clientsEnabledForCompany.value) {
      loadClients();
    }
    loadAccountCodes();
    loadInvoices();
    loadPaymentCodes(company);
  } else {
    loadPaymentCodes(null);
    form.value = {
      name: '',
      alias_names: [],
      vat_number: '',
      address: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      website: '',
      registration_number: '',
      tax_office: '',
      default_debit_account: '',
      default_credit_account: '',
      vat_account: '',
      is_active: true,
      validate_company_on_scan: false
    };
  }
}, { immediate: true });

const resetPaymentCodes = () => {
  loadPaymentCodes(null);
};

const savePaymentCodes = async () => {
  if (!props.company) return;

  savingPaymentCodes.value = true;
  try {
    // The update endpoint validates the whole company, so the required fields
    // travel with the codes.
    const { data } = await api.put(`/api/companies/${props.company.id}`, {
      ...form.value,
      alias_names: form.value.alias_names || [],
      payment_type_codes: paymentTypeCodes.value,
    });
    loadPaymentCodes(data);
    emit('saved');
  } catch (error) {
    console.error('Failed to save payment codes:', error);
    alert(error.response?.data?.message || t('paymentCodes.saveFailed'));
  } finally {
    savingPaymentCodes.value = false;
  }
};

const addAlias = () => {
  const alias = newAlias.value.trim();
  if (alias && !form.value.alias_names.includes(alias)) {
    form.value.alias_names.push(alias);
    newAlias.value = '';
  }
};

const removeAlias = (index) => {
  form.value.alias_names.splice(index, 1);
};

const handleSubmit = async () => {
  try {
    saving.value = true;

    if (props.company) {
      await api.put(`/api/companies/${props.company.id}`, form.value);
    } else {
      await api.post('/api/companies', form.value);
    }

    emit('saved');
  } catch (error) {
    console.error('Failed to save company:', error);
    alert('Failed to save company. Please try again.');
  } finally {
    saving.value = false;
  }
};

const editVendor = (vendor) => {
  editingVendor.value = vendor;
  showVendorModal.value = true;
};

const closeVendorModal = () => {
  editingVendor.value = null;
  showVendorModal.value = false;
};

const handleVendorSaved = () => {
  closeVendorModal();
  loadVendors();
};

const handleVendorBulkImported = () => {
  showVendorBulkImportModal.value = false;
  loadVendors();
};

const editClient = (client) => {
  editingClient.value = client;
  showClientModal.value = true;
};

const closeClientModal = () => {
  editingClient.value = null;
  showClientModal.value = false;
};

const handleClientSaved = () => {
  closeClientModal();
  loadClients();
};

const handleClientBulkImported = () => {
  showClientBulkImportModal.value = false;
  loadClients();
};

const deleteClient = async (client) => {
  if (!confirm(`Are you sure you want to delete ${client.vendor_name}?`)) {
    return;
  }

  try {
    await api.delete(`/api/companies/${props.company.id}/vendors/${client.id}`);
    loadClients();
  } catch (error) {
    console.error('Failed to delete client:', error);
    alert('Failed to delete client. Please try again.');
  }
};

const editAccountCode = (code) => {
  console.log('[CompanyModalEnhanced] editAccountCode called with:', {
    hasId: !!code?.id,
    id: code?.id,
    account_code: code?.account_code,
    account_name: code?.account_name,
    fullCode: code
  });
  editingAccountCode.value = code;
  showAccountCodeModal.value = true;
};

const closeAccountCodeModal = () => {
  editingAccountCode.value = null;
  showAccountCodeModal.value = false;
};

// The table lists grouped codes and edits group.entries[0], so the arrows walk the
// same groups in the same order rather than the flat, ungrouped list behind them.
const currentAccountCodeIndex = computed(() => {
  if (!editingAccountCode.value) return -1;
  return groupedAccountCodes.value.findIndex(group => group.ids.includes(editingAccountCode.value.id));
});

const canGoPreviousAccountCode = computed(() => currentAccountCodeIndex.value > 0);
const canGoNextAccountCode = computed(() =>
  currentAccountCodeIndex.value > -1 && currentAccountCodeIndex.value < groupedAccountCodes.value.length - 1
);

const navigateAccountCode = (step) => {
  const target = currentAccountCodeIndex.value + step;
  if (currentAccountCodeIndex.value === -1 || target < 0 || target >= groupedAccountCodes.value.length) {
    return;
  }
  editingAccountCode.value = groupedAccountCodes.value[target].entries[0];
};

const handleAccountCodeSaved = () => {
  closeAccountCodeModal();
  loadAccountCodes();
};

const handleBulkImported = () => {
  showBulkImportModal.value = false;
  loadAccountCodes();
};

const editCodeMapping = (code) => {
  editingCodeMapping.value = code;
  showCodeMappingModal.value = true;
};

const closeCodeMappingModal = () => {
  editingCodeMapping.value = null;
  showCodeMappingModal.value = false;
};

const handleCodeMappingSaved = () => {
  closeCodeMappingModal();
  loadAccountCodes();
};

const deleteCodeMapping = async (code) => {
  if (!confirm(`Are you sure you want to delete the mapping for barcode ${code.barcode_mapping}?`)) {
    return;
  }

  try {
    // Remove the barcode mapping by updating with null
    await api.put(`/api/companies/${props.company.id}/account-codes/${code.id}`, {
      ...code,
      barcode_mapping: null
    });
    loadAccountCodes();
  } catch (error) {
    console.error('Failed to delete code mapping:', error);
    alert('Failed to delete mapping. Please try again.');
  }
};

const deleteVendor = async (vendor) => {
  if (!confirm(`Are you sure you want to delete ${vendor.vendor_name}?`)) {
    return;
  }

  try {
    await api.delete(`/api/companies/${props.company.id}/vendors/${vendor.id}`);
    loadVendors();
  } catch (error) {
    console.error('Failed to delete vendor:', error);
    alert('Failed to delete vendor. Please try again.');
  }
};

const deleteAccountCode = async (code) => {
  if (!confirm(`Are you sure you want to delete account code ${code.account_code}?`)) {
    return;
  }

  try {
    await api.delete(`/api/account-codes/${code.id}`);
    loadAccountCodes();
    selectedAccountCodes.value = [];
  } catch (error) {
    console.error('Failed to delete account code:', error);
    alert('Failed to delete account code. Please try again.');
  }
};

const deleteAccountCodeGroup = async (group) => {
  const count = group.entries.length;
  const message = count > 1 
    ? `Are you sure you want to delete account code "${group.account_code}" and all its ${count} entries?`
    : `Are you sure you want to delete account code "${group.account_code}"?`;
    
  if (!confirm(message)) {
    return;
  }

  try {
    // Delete all entries in the group
    await Promise.all(group.ids.map(id => api.delete(`/api/account-codes/${id}`)));
    loadAccountCodes();
    selectedAccountCodes.value = [];
  } catch (error) {
    console.error('Failed to delete account code group:', error);
    alert('Failed to delete account codes. Please try again.');
  }
};

const toggleGroupSelection = (ids, event) => {
  if (event.target.checked) {
    // Add all IDs from the group
    ids.forEach(id => {
      if (!selectedAccountCodes.value.includes(id)) {
        selectedAccountCodes.value.push(id);
      }
    });
  } else {
    // Remove all IDs from the group
    selectedAccountCodes.value = selectedAccountCodes.value.filter(id => !ids.includes(id));
  }
};

const toggleSelectAll = (event) => {
  if (event.target.checked) {
    selectedAccountCodes.value = filteredAccountCodes.value.map(code => code.id);
  } else {
    selectedAccountCodes.value = [];
  }
};

const bulkDeleteAccountCodes = async () => {
  if (!confirm(`Are you sure you want to delete ${selectedAccountCodes.value.length} account code(s)?`)) {
    return;
  }

  try {
    await api.post(`/api/companies/${props.company.id}/account-codes/bulk-delete`, {
      ids: selectedAccountCodes.value
    });
    loadAccountCodes();
    selectedAccountCodes.value = [];
  } catch (error) {
    console.error('Failed to delete account codes:', error);
    alert('Failed to delete account codes');
  }
};

const toggleSelectAllVendors = () => {
  if (selectedVendors.value.length === filteredVendors.value.length) {
    selectedVendors.value = [];
  } else {
    selectedVendors.value = filteredVendors.value.map(v => v.id);
  }
};

const toggleVendorSelection = (id, event) => {
  if (event.target.checked) {
    if (!selectedVendors.value.includes(id)) {
      selectedVendors.value.push(id);
    }
  } else {
    selectedVendors.value = selectedVendors.value.filter(x => x !== id);
  }
};

const bulkDeleteVendors = async () => {
  if (!confirm(`Are you sure you want to delete ${selectedVendors.value.length} vendor(s)?`)) {
    return;
  }

  try {
    await api.post(`/api/companies/${props.company.id}/vendors/bulk-delete`, {
      ids: selectedVendors.value
    });
    selectedVendors.value = [];
    await loadVendors();
  } catch (error) {
    console.error('Failed to delete vendors:', error);
    alert('Failed to delete vendors');
  }
};

const toggleSelectAllClients = () => {
  if (selectedClients.value.length === filteredClients.value.length) {
    selectedClients.value = [];
  } else {
    selectedClients.value = filteredClients.value.map(c => c.id);
  }
};

const toggleClientSelection = (id, event) => {
  if (event.target.checked) {
    if (!selectedClients.value.includes(id)) {
      selectedClients.value.push(id);
    }
  } else {
    selectedClients.value = selectedClients.value.filter(x => x !== id);
  }
};

const bulkDeleteClients = async () => {
  if (!confirm(`Are you sure you want to delete ${selectedClients.value.length} client(s)?`)) {
    return;
  }

  try {
    await api.post(`/api/companies/${props.company.id}/vendors/bulk-delete`, {
      ids: selectedClients.value
    });
    selectedClients.value = [];
    await loadClients();
  } catch (error) {
    console.error('Failed to delete clients:', error);
    alert('Failed to delete clients');
  }
};

const handleAliasAdded = () => {
  showSaveAsAliasModal.value = false;
  emit('saved');
  emit('close');
};

const toggleSelectAllInvoices = () => {
  if (selectedInvoices.value.length === filteredInvoices.value.length) {
    selectedInvoices.value = [];
  } else {
    selectedInvoices.value = filteredInvoices.value.map(i => i.id);
  }
};

const isDateSelected = (group) => {
  // Check if all invoices in this date group are selected
  const groupInvoiceIds = group.invoices.map(invoice => invoice.id);
  return groupInvoiceIds.length > 0 &&
         groupInvoiceIds.every(id => selectedInvoices.value.includes(id));
};

const isDatePartiallySelected = (group) => {
  // Check if some but not all invoices in this date group are selected
  const groupInvoiceIds = group.invoices.map(invoice => invoice.id);
  if (groupInvoiceIds.length === 0) return false;
  
  const selectedCount = groupInvoiceIds.filter(id =>
    selectedInvoices.value.includes(id)
  ).length;
  
  return selectedCount > 0 && selectedCount < groupInvoiceIds.length;
};

const toggleDateSelection = (group) => {
  const groupInvoiceIds = group.invoices.map(invoice => invoice.id);
  
  if (isDateSelected(group)) {
    // Remove all invoices from this date
    selectedInvoices.value = selectedInvoices.value.filter(
      id => !groupInvoiceIds.includes(id)
    );
  } else {
    // Add all invoices from this date (avoid duplicates)
    const newSelection = [...selectedInvoices.value];
    groupInvoiceIds.forEach(id => {
      if (!newSelection.includes(id)) {
        newSelection.push(id);
      }
    });
    selectedInvoices.value = newSelection;
  }
};

const archiveInvoice = async (invoice) => {
  if (!confirm(`Are you sure you want to archive invoice ${invoice.invoice_number}?`)) {
    return;
  }

  try {
    await api.post(`/api/invoices/${invoice.id}/archive`);
    loadInvoices();
  } catch (error) {
    console.error('Failed to archive invoice:', error);
    alert('Failed to archive invoice');
  }
};

const bulkArchiveInvoices = async () => {
  if (!confirm(`Are you sure you want to archive ${selectedInvoices.value.length} invoice(s)?`)) {
    return;
  }

  try {
    await api.post(`/api/invoices/bulk-archive`, {
      ids: selectedInvoices.value
    });
    loadInvoices();
    selectedInvoices.value = [];
  } catch (error) {
    console.error('Failed to archive invoices:', error);
    alert('Failed to archive invoices');
  }
};

const bulkDownloadInvoices = async () => {
  if (selectedInvoices.value.length === 0) {
    return;
  }

  try {
    console.log('📥 Starting bulk ZIP download for', selectedInvoices.value.length, 'invoices');
    console.log('📋 Invoice IDs:', selectedInvoices.value);

    // Download all invoices as a single ZIP file
    const response = await api.post('/api/scans/download/bulk-originals', {
      invoice_ids: selectedInvoices.value
    }, {
      responseType: 'blob'
    });

    // Create download link for ZIP file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `invoices_${timestamp}.zip`;
    link.download = filename;

    console.log(`💾 Downloading ZIP: ${filename}`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('✅ Bulk ZIP download completed');

    // Clear selection after download
    selectedInvoices.value = [];
  } catch (error) {
    console.error('❌ Failed to download invoices:', error);
    console.error('❌ Error status:', error.response?.status);

    // Show user-friendly error message
    let errorMessage = 'Failed to download invoices';

    // If error response is a Blob (because we set responseType: 'blob'), read it
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        console.error('❌ Error response (parsed):', text);
        const errorData = JSON.parse(text);

        if (error.response?.status === 422) {
          // Validation error
          if (errorData.errors) {
            const errors = Object.values(errorData.errors).flat();
            errorMessage = 'Validation error: ' + errors.join(', ');
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        console.error('❌ Failed to parse error blob:', e);
      }
    } else if (error.response?.status === 422) {
      // Non-blob validation error
      if (error.response.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = 'Validation error: ' + errors.join(', ');
      } else {
        errorMessage = 'Invalid invoice selection. Please try again.';
      }
    } else if (error.response?.status === 404) {
      errorMessage = 'No valid invoice files found to download';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    alert(errorMessage);
  }
};

const viewInvoice = (invoice) => {
  // Open preview modal instead of navigating
  previewInvoice.value = invoice;
  showInvoicePreview.value = true;
};

const closePreview = () => {
  showInvoicePreview.value = false;
  previewInvoice.value = null;
};

const printInvoice = () => {
  if (!previewInvoice.value) return;
  
  // Create a new window for printing
  const printWindow = window.open('', '', 'width=800,height=600');
  const imageUrl = `/api/invoices/${previewInvoice.value.id}/original?thumbnail=false`;
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${previewInvoice.value.invoice_number || 'Invoice'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          html, body { width: 100%; height: 100%; background: white; }
          body { display: flex; align-items: center; justify-content: center; }
          img { max-width: 100%; max-height: 100%; object-fit: contain; }
        </style>
      </head>
      <body>
        <img src="${imageUrl}" alt="Invoice" />
      </body>
    </html>
  `);
  
  printWindow.document.close();
  
  // Wait for image to load then print
  setTimeout(() => {
    printWindow.print();
  }, 500);
};
const exportInvoice = async (invoice) => {
  try {
    const response = await api.get(`/api/invoices/${invoice.id}/original`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${invoice.invoice_number || 'invoice'}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export invoice:', error);
    alert('Failed to export invoice');
  }
};

const deleteInvoice = async (invoice) => {
  if (!confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}?`)) {
    return;
  }

  try {
    await api.delete(`/api/invoices/${invoice.id}`);
    loadInvoices();
  } catch (error) {
    console.error('Failed to delete invoice:', error);
    alert('Failed to delete invoice');
  }
};

const exportToXero = async () => {
  if (selectedInvoices.value.length === 0) {
    return;
  }

  try {
    console.log('📊 Starting Xero CSV export for', selectedInvoices.value.length, 'invoices');

    // Get full invoice details for selected invoices
    const selectedInvoiceData = invoices.value.filter(inv => 
      selectedInvoices.value.includes(inv.id)
    );

    // Xero CSV Header
    const headers = [
      '*ContactName', 'EmailAddress', 'POAddressLine1', 'POAddressLine2', 'POAddressLine3', 'POAddressLine4',
      'POCity', 'PORegion', 'POPostalCode', 'POCountry', '*InvoiceNumber', 'Reference', '*InvoiceDate',
      '*DueDate', 'Total', 'InventoryItemCode', '*Description', '*Quantity', '*UnitAmount', 'Discount',
      '*AccountCode', '*TaxType', 'TaxAmount', 'TrackingName1', 'TrackingOption1', 'TrackingName2',
      'TrackingOption2', 'Currency', 'BrandingTheme'
    ];

    // Generate CSV rows
    const rows = [headers.join(',')];

    selectedInvoiceData.forEach(invoice => {
      const row = [
        `"${invoice.vendor_name || ''}"`,  // ContactName
        '',  // EmailAddress
        '',  // POAddressLine1
        '',  // POAddressLine2
        '',  // POAddressLine3
        '',  // POAddressLine4
        '',  // POCity
        '',  // PORegion
        '',  // POPostalCode
        '',  // POCountry
        `"${invoice.invoice_number || ''}"`,  // InvoiceNumber
        '',  // Reference
        invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-GB') : '',  // InvoiceDate (DD/MM/YYYY)
        invoice.invoice_date ? new Date(new Date(invoice.invoice_date).setDate(new Date(invoice.invoice_date).getDate() + 30)).toLocaleDateString('en-GB') : '',  // DueDate (+30 days)
        invoice.total_amount || '',  // Total
        '',  // InventoryItemCode
        `"Invoice ${invoice.invoice_number || ''}"`,  // Description
        '1',  // Quantity
        invoice.total_amount || '',  // UnitAmount
        '',  // Discount
        '200',  // AccountCode (default sales account)
        '20% (VAT on Income)',  // TaxType
        '',  // TaxAmount
        '',  // TrackingName1
        '',  // TrackingOption1
        '',  // TrackingName2
        '',  // TrackingOption2
        invoice.currency || 'BGN',  // Currency
        ''   // BrandingTheme
      ];
      rows.push(row.join(','));
    });

    // Create CSV blob and download
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `xero_export_${timestamp}.csv`;
    link.download = filename;

    console.log(`💾 Downloading Xero CSV: ${filename}`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('✅ Xero CSV export completed');

    // Clear selection after export
    selectedInvoices.value = [];
  } catch (error) {
    console.error('❌ Failed to export to Xero:', error);
    alert('Failed to export invoices to Xero format');
  }
};

const bulkDeleteInvoices = async () => {
  if (selectedInvoices.value.length === 0) {
    return;
  }

  const count = selectedInvoices.value.length;
  const confirmMessage = count === 1
    ? `Are you sure you want to delete this invoice?`
    : `Are you sure you want to delete ${count} invoices?`;

  if (!confirm(confirmMessage)) {
    return;
  }

  try {
    console.log('🗑️ Starting bulk delete for', count, 'invoices');
    console.log('📋 Invoice IDs:', selectedInvoices.value);

    // Delete all selected invoices
    await Promise.all(
      selectedInvoices.value.map(id => api.delete(`/api/invoices/${id}`))
    );

    console.log('✅ Bulk delete completed');

    // Clear selection and reload invoices
    selectedInvoices.value = [];
    await loadInvoices();
  } catch (error) {
    console.error('❌ Failed to delete invoices:', error);
    alert('Failed to delete some invoices. Please try again.');
    // Reload to show current state
    await loadInvoices();
  }
};


const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString();
};

const formatDateShort = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatAmount = (amount, currency = 'BGN') => {
  if (!amount) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'BGN'
  }).format(amount);
};

const formatAmountValue = (amount) => {
  if (!amount) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Clear all filters
const clearFilters = () => {
  invoiceSearch.value = '';
  filterStatus.value = '';
  filterVendor.value = '';
  filterType.value = '';
  filterVerified.value = '';
  filterDateFrom.value = '';
  filterDateTo.value = '';
  filterUploadDate.value = '';
  filterArchived.value = '';
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content-large {
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.tab-navigation {
  display: flex;
  gap: 8px;
  padding: 0 24px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button:hover {
  color: var(--text-primary);
  background: var(--bg-gray);
}

.tab-button.active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.tab-content {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
}

.tab-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.filter-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
}

.search-box {
  position: relative;
  flex: 0 1 300px;
  min-width: 200px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 14px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-light);
}

.search-input::placeholder {
  color: var(--text-secondary);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: var(--danger);
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"],
.form-group input[type="url"] {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-top: 12px;
  margin-bottom: 8px;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
}

.checkbox-label span {
  font-size: 14px;
  line-height: 18px;
  user-select: none;
  margin-left: 2px;
}

.toggle-label {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-top: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  width: auto;
}

.form-group-toggle {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.toggle-text {
  font-size: 14px;
  line-height: 20px;
  user-select: none;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ccc;
  border-radius: 10px;
  transition: background 0.2s;
}

.toggle-track::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle input:checked + .toggle-track {
  background: var(--primary, #4f8cff);
}

.toggle input:checked + .toggle-track::before {
  transform: translateX(16px);
}

.alias-names-container {
  width: 100%;
}

.alias-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: white;
  min-height: 42px;
  align-items: center;
}

.alias-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 500;
}

.alias-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 18px;
  cursor: pointer;
  border-radius: 3px;
}

.alias-remove:hover {
  background: #dbeafe;
  color: #1e40af;
}

.alias-input {
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  padding: 4px 6px;
  font-size: 14px;
}

.field-hint {
  margin: 6px 0 0 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.form-section-title {
  margin: 32px 0 20px 0;
  padding-top: 24px;
  border-top: 1px solid var(--border);
}

.form-section-title h4 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.form-section-title .section-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.help-text {
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
}

.font-semibold {
  font-weight: 600;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

/* Internal code column in the account-codes list */
.internal-column { width: 130px; white-space: nowrap; }

.internal-badge {
  display: inline-block;
  margin-right: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* Goods and services get their own colour: the two are told apart constantly. */
.internal-badge-s { background: #e0f2fe; color: #075985; }
.internal-badge-u { background: #fef3c7; color: #92400e; }

.internal-empty { color: #d1d5db; }

/* Payment codes tab */
.pc-intro {
  margin: 0 0 18px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--text-secondary, #6b7280);
  max-width: 70ch;
}

.pc-table { display: flex; flex-direction: column; }

.pc-row {
  display: grid;
  grid-template-columns: 1fr 180px 120px;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid var(--border);
}

.pc-row.pc-head {
  border-top: none;
  padding-bottom: 6px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-secondary, #6b7280);
}

.pc-method { font-size: 14px; color: var(--text-primary, #111827); }

.pc-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
}
.pc-input:focus { outline: none; border-color: #2b82f0; }

.pc-default {
  font-size: 13px;
  color: var(--text-secondary, #9ca3af);
  font-variant-numeric: tabular-nums;
}

.pc-hint {
  margin: 16px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--text-secondary, #6b7280);
  max-width: 70ch;
}

@media (max-width: 640px) {
  .pc-row { grid-template-columns: 1fr 120px; }
  .pc-row > :nth-child(3) { display: none; }
}

.footer-left,
.footer-right {
  display: flex;
  gap: 12px;
}

.btn-save-as-alias,
.btn-add,
.btn-secondary,
.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save-as-alias {
  background: white;
  border: 1px solid #f59e0b;
  color: #f59e0b;
}

.btn-save-as-alias:hover {
  background: #fef3c7;
}

.btn-add {
  background: var(--primary);
  border: none;
  color: white;
}

.btn-add:hover {
  background: var(--primary-hover);
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-gray);
}

.btn-primary {
  background: var(--primary);
  border: none;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: var(--text-secondary);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.vendors-table,
.account-codes-table,
.code-mapping-table {
  overflow-x: auto;
}

.bulk-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: transparent;
  border-left: none;
  border-radius: 8px;
  margin-bottom: 16px;
}

.selection-count {
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.checkbox-column {
  width: 40px;
  text-align: center;
}

.checkbox-column input[type="checkbox"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.barcode-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.mapping-badge {
  display: inline-block;
  color: #0f172a;
  font-size: 14px;
  font-weight: 400;
}

.text-muted {
  color: #9ca3af;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-gray);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

td {
  padding: 16px;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
  vertical-align: middle;
}

th.actions-column {
  width: 120px;
  min-width: 120px;
  text-align: center;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: var(--bg-secondary, #f3f4f6);
}

.sort-icon {
  margin-left: 6px;
  font-size: 12px;
  color: var(--text-tertiary, #9ca3af);
  transition: color 0.15s ease;
}

.sort-icon.active {
  color: var(--primary, #4a90e2);
}

td.actions-column {
  width: 120px;
  min-width: 120px;
  white-space: nowrap;
  text-align: center;
  vertical-align: middle;
}

.actions-wrapper {
  display: inline-flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.btn-icon.btn-icon-danger:hover {
  background: var(--danger-light);
  border-color: var(--danger);
  color: var(--danger);
}

/* Invoice Action Buttons */
.invoice-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-action svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
}

.btn-view {
  background: #0ea5e9;
  color: white;
}

.btn-view:hover {
  background: #0284c7;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-export {
  background: #10b981;
  color: white;
}

.btn-export:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-action.btn-delete {
  background: #ef4444;
  color: white;
}

.btn-action.btn-delete:hover {
  background: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Invoice Preview Modal */
.invoice-preview-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.invoice-preview-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.preview-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.preview-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-print {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--primary, #4a90e2);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-print:hover {
  background: var(--primary-hover, #357abd);
  opacity: 0.9;
}

.btn-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-gray);
  border-color: var(--text-primary);
  color: var(--text-primary);
}

.preview-content {
  flex: 1;
  overflow: auto;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray, #f8f8f8);
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 16px;
}

@print {
  /* This @print is for fallback only - main printing happens in separate window */
  body {
    margin: 0;
    padding: 0;
    background: white;
  }

  .invoice-preview-modal-overlay {
    background: white;
    width: 100%;
    height: 100%;
  }

  .invoice-preview-modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    box-shadow: none;
    border: none;
    border-radius: 0;
  }

  .preview-header {
    display: none !important;
  }

  .preview-content {
    padding: 0;
    background: white;
    height: 100%;
  }

  .preview-image {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border: none;
    box-shadow: none;
    border-radius: 0;
  }
}

@media (max-width: 640px) {
  .modal-overlay {
    padding: 0;
  }

  .modal-content-large {
    max-width: 100%;
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
    margin: 0;
  }

  .modal-header {
    padding: 16px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .modal-body {
    padding: 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .tab-navigation {
    overflow-x: auto;
    padding: 0 8px;
    gap: 4px;
  }

  .tab-button {
    font-size: 12px;
    padding: 8px 12px;
    white-space: nowrap;
  }

  .tab-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
    gap: 8px;
  }

  .search-box {
    flex: 1;
    width: 100%;
    max-width: 100%;
  }

  .btn-add {
    width: 100%;
    justify-content: center;
  }

  .footer-left,
  .footer-right {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .modal-footer {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 1px solid var(--border);
  }

  .btn-save,
  .btn-cancel,
  .btn-primary,
  .btn-secondary,
  .btn-save-as-alias {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
  }

  .form-group label {
    font-size: 13px;
  }

  .form-group input {
    padding: 10px 12px;
    font-size: 14px;
  }

}

/* Invoices Table Styles */
.invoices-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border);
}

.select-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.select-all-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-all-section input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.select-all-section label {
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
}

.bulk-actions {
  display: flex;
  gap: 8px;
}

.btn-bulk-archive {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  height: 45px;
}

.btn-bulk-archive:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-bulk-download {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  height: 45px;
}

.btn-bulk-download:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-bulk-export {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  height: 45px;
}

.btn-bulk-export:hover {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
}

.btn-bulk-delete {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  height: 45px;
}

.btn-bulk-delete:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.table-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.scan-count {
  font-size: 14px;
  color: #64748b;
}

.date-separator {
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.date-separator-row {
  background: #f8fafc !important;
}

.date-separator-row td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.date-separator-row:hover {
  background: #f8fafc !important;
}

.date-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin: 0;
}

.date-label {
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
}

.date-count {
  font-size: 12px;
  color: #64748b;
  margin-left: 4px;
}

.invoices-table {
  overflow-x: auto;
  max-width: 100%;
}

.invoices-table table {
  width: 100%;
  border-collapse: collapse;
}

.invoices-table th,
.invoices-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.invoices-table thead {
  background: #f8fafc;
}

.invoices-table th {
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.invoices-table td {
  font-size: 14px;
  color: #0f172a;
}

.invoices-table tbody tr {
  transition: background 0.15s ease;
}

.invoices-table tbody tr:hover {
  background: #f8fafc;
}

.checkbox-column {
  width: 50px;
  text-align: center;
}

.checkbox-column input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.actions-column {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
}

.invoices-table tbody tr {
  transition: background 0.15s ease;
}

.invoices-table tbody tr:hover {
  background: #f8fafc;
}

.checkbox-column {
  width: 50px;
  text-align: center;
}

.checkbox-column input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.actions-column {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;
}

.btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.btn-icon.btn-icon-danger:hover {
  background: var(--danger-light);
  border-color: var(--danger);
  color: var(--danger);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-processed {
  background: #d1fae5;
  color: #065f46;
}

.status-archived {
  background: #e5e7eb;
  color: #374151;
}

.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

/* Filter Row Styles */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
  flex: 1;
  flex-basis: 0; /* Ensure equal distribution */
}

.filter-item label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-select,
.filter-input {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: all 0.2s ease;
  width: 100%; /* Ensure full width */
  box-sizing: border-box; /* Include padding in width */
  max-width: 100%; /* Prevent overflow */
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.filter-select {
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-select option {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Specific width for date inputs */
#filterDateFrom,
#filterDateTo {
  width: 151px;
  min-width: 151px;
  max-width: 151px;
}

/* Ensure date filter items maintain proper flex behavior */
.filter-item:has(#filterDateFrom),
.filter-item:has(#filterDateTo) {
  flex: 0 0 auto; /* Don't grow or shrink */
  flex-basis: 151px; /* Base width matches input */
}

.btn-clear-filters {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 11px 18px;
  border: 2px solid var(--border);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-top: 20px;
  align-self: flex-start;
}

.btn-clear-filters:hover {
  border-color: #ef4444;
  color: white;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-clear-filters svg {
  flex-shrink: 0;
  font-size: 16px;
}

/* Responsive adjustments */
@media (max-width: 950px) {
  .invoices-table {
    overflow-x: visible;
  }

  .invoices-table table {
    display: block;
  }

  .invoices-table thead {
    display: none;
  }

  .invoices-table tbody {
    display: block;
  }

  .invoices-table tbody tr {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .invoices-table tbody tr:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .date-separator-row {
    border: none !important;
    box-shadow: none !important;
    background: #f8fafc !important;
    padding: 8px 12px !important;
    border-radius: 8px !important;
    margin-bottom: 8px !important;
  }

  .invoices-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: none;
  }

  .invoices-table td:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }

  .invoices-table td::before {
    content: attr(data-label);
    font-weight: 600;
    font-size: 12px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .checkbox-column {
    width: 100%;
    justify-content: flex-start;
  }

  .checkbox-column::before {
    content: 'Select';
  }

  .actions-column {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    flex-direction: row;
    justify-content: flex-end;
    padding: 12px 0 0 0;
    border-top: 1px solid #e2e8f0 !important;
  }

  .actions-column::before {
    content: none;
  }

  .invoice-actions {
    display: flex;
    gap: 8px;
    width: 100%;
    justify-content: flex-end;
  }

  .btn-action {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    border-radius: 10px;
  }

  .btn-action svg {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 768px) {
  .filters-row {
    flex-direction: column;
    gap: 8px;
    padding: 12px;
  }
  
  .filter-item {
    min-width: 100%;
    gap: 4px;
  }

  .filter-item label {
    font-size: 11px;
  }

  .filter-select,
  .filter-input {
    padding: 10px 12px;
    font-size: 14px;
  }

  #filterDateFrom,
  #filterDateTo {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  .filter-item:has(#filterDateFrom),
  .filter-item:has(#filterDateTo) {
    flex-basis: 100%;
  }

  .btn-clear-filters {
    width: 100%;
    padding: 12px;
    justify-content: center;
  }
  
  .btn-clear-filters {
    align-self: stretch;
    justify-content: center;
  }
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* A field with a mark standing in it keeps the room for the mark: the
   tighter padding above would have put the text under the magnifier. */
.search-box input,
.search-input,
.modal-search-input-wrapper input {
    padding-left: 30px;
}

/* The row actions inside the tables, drawn like the dashboard's: the mark in
   its own colour with nothing behind it, and the hover is a two-pixel lift.
   They were filled squares — blue, green and red — three per row. */
.btn-action,
.btn-action.btn-view,
.btn-action.btn-export,
.btn-action.btn-delete {
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    box-shadow: none;
}

.btn-action svg {
    width: 14px;
    height: 14px;
}

.btn-action.btn-view { color: #0697e4; }
.btn-action.btn-export { color: #0f9d6f; }
.btn-action.btn-delete { color: #e04545; }

.btn-action:hover,
.btn-action.btn-view:hover,
.btn-action.btn-export:hover,
.btn-action.btn-delete:hover {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    transform: translateY(-2px);
}

/* Outweighing the field's own padding, which is written as .search-box
   .search-input and so outranks a plain descendant rule. */
.search-box input.search-input,
.modal-search-input-wrapper input {
    padding-left: 32px;
}

/* Every mark-on-its-own in this dialogue — the row actions, the close — is
   the dashboard's: no box, the mark in its own colour, and the hover is a
   two-pixel lift. They were grey tiles with a hairline around them. */
.btn-icon {
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #0697e4;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-icon svg {
    width: 14px;
    height: 14px;
}

.btn-icon.btn-icon-danger {
    color: #e04545;
}

.btn-icon:hover {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    transform: translateY(-2px);
}

.modal-header .btn-icon {
    color: #64748b;
}

/* The preview sheet and the descriptions sheet, same frame as the rest. */
.invoice-preview-modal,
.descriptions-modal {
  border: 1px solid #dde3ea;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.preview-header,
.descriptions-modal-header {
  padding: 12px 14px;
  border-bottom: 1px solid #dde3ea;
}

.preview-header h3,
.descriptions-modal-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.descriptions-modal-body {
  padding: 12px 14px;
}

.descriptions-modal-footer {
  padding: 10px 14px;
  gap: 8px;
  border-top: 1px solid #dde3ea;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* A sortable heading and its mark on one line. The <th> is a table cell, so
   the icon followed the text onto the next line; the dashboard sets the pair
   side by side and so does this. */
.sortable-header {
  white-space: nowrap;
}

.sortable-header svg {
  display: inline-block;
  vertical-align: -1px;
  margin-left: 5px;
  color: #94a3b8;
}

/* ── The tabs, and the switch ───────────────────────────────────────────
 *
 * The tabs were 14px labels in a 12/20 box with a 3px underline; at the
 * dialogue's scale that is a band as tall as its heading. And the switch was
 * a solid blue pill with a white knob — the loudest mark in a card of
 * hairlines. Both are now what the companies page settled on. */
.tab-navigation {
  padding: 0 8px;
  border-bottom: 1px solid #dde3ea;
  gap: 2px;
}

.tab-button {
  gap: 6px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}

.tab-button svg {
  color: currentColor;
  transition: transform 0.15s ease;
}

.tab-button:hover svg {
  transform: scale(1.18);
}

/* A rail with a handle on it, not a filled pill: grey to the left when off,
   blue to the right when on. */
.toggle {
  width: 30px;
  height: 17px;
}

.toggle-track {
  background: transparent;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  box-shadow: none;
}

.toggle-track::before {
  content: '';
  position: absolute;
  height: 9px;
  width: 9px;
  left: 2.5px;
  top: 50%;
  margin-top: -4.5px;
  background: #94a3b8;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.toggle input:checked + .toggle-track {
  background: transparent;
  border-color: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  background: var(--primary);
  transform: translateX(13px);
}

.toggle:hover .toggle-track {
  border-color: var(--primary);
}

.toggle-text,
.toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

/* The payment-code table, at the same scale as the rest. */
.pc-row {
  font-size: 12px;
}

.pc-head {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}

.pc-input {
  padding: 5px 8px;
  border: 1px solid #dde3ea;
  border-radius: 6px;
  font-size: 12px;
}

.pc-intro,
.pc-hint {
  font-size: 11px;
  color: #64748b;
}

/* The tables inside the tabs. */
.tab-header h3 {
  font-size: 14px;
  font-weight: 700;
}

.vendors-table th,
.account-codes-table th {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.vendors-table td,
.account-codes-table td {
  font-size: 12px;
}

.select-row {
  font-size: 11px;
}

.empty-state p {
  font-size: 12px;
  color: #64748b;
}

/* ── The rest of the buttons ───────────────────────────────────────────
 *
 * The chrome above settles the ones named primary, secondary, cancel and
 * save; these carry names of their own and so kept the old 15px-in-a-49px-box
 * shape. Same geometry as the others: a 32px box, a 13px label, 8px corner,
 * and a filled face only where something is being committed. */
.btn-save-as-alias,
.btn-bulk-download,
.btn-print,
.btn-clear-filters,
.btn-bulk-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
  transition: all 0.15s ease;
  width: auto;
}

.btn-save-as-alias,
.btn-bulk-download,
.btn-print,
.btn-clear-filters {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #1e293b;
}

.btn-save-as-alias:hover:not(:disabled),
.btn-bulk-download:hover:not(:disabled),
.btn-print:hover:not(:disabled),
.btn-clear-filters:hover:not(:disabled) {
  background: #fff;
  border-color: #0697e4;
  color: #0697e4;
  box-shadow: none;
  transform: none;
}

.btn-bulk-delete {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #e04545;
}

.btn-bulk-delete:hover:not(:disabled) {
  background: #fff;
  border-color: #e04545;
  color: #e04545;
  box-shadow: none;
  transform: none;
}


/* The deleting mark hovers like every other mark: it turns its own colour
   and lifts, and nothing is drawn behind it. Two earlier rules in this file
   gave it a red box with a red edge — written as .btn-icon.btn-icon-danger,
   which outranks a plain .btn-icon:hover, so they are answered in kind. */
.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── The switch, the chips and the hints ───────────────────────────────
 *
 * The switch was a solid blue pill with a white knob — the loudest mark in a
 * card of hairlines — and the alternative names sat in blue-on-blue chips
 * with an 18px typographic cross. All three are now what the rest of the
 * product uses: a rail with a handle, a quiet chip with a hairline, and a
 * hint one step smaller than the field it explains. */
.toggle {
  width: 30px;
  height: 17px;
}

.toggle-track {
  background: transparent;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  box-shadow: none;
}

.toggle-track::before,
.toggle-track::after {
  content: '';
  position: absolute;
  height: 9px;
  width: 9px;
  left: 2.5px;
  right: auto;
  top: 50%;
  margin-top: -4.5px;
  background: #94a3b8;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: none;
}

.toggle-track::after {
  content: none;
}

.toggle input:checked + .toggle-track {
  background: transparent;
  border-color: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  background: var(--primary);
  transform: translateX(13px);
}

.toggle:hover .toggle-track {
  border-color: var(--primary);
}

.toggle-text,
.toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

/* An alternative name on file: a quiet chip, not a blue badge. */
.alias-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px 2px 8px;
  background: #fff;
  border: 1px solid #dde3ea;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.alias-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-size: 13px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}

.alias-remove:hover {
  background: transparent;
  color: #e04545;
  transform: translateY(-1px);
}

.alias-tags {
  gap: 5px;
}

.alias-input {
  font-size: 13px;
}

/* What a field means, a step below the field itself. */
.field-hint {
  font-size: 11px;
  line-height: 1.4;
  color: #94a3b8;
}

.form-group {
  margin-bottom: 10px;
}

.form-group label {
  margin-bottom: 3px;
}
</style>
